// Client-side PowerPoint export for Get Who To Buy Strategy Tool Result
import pptxgen from 'pptxgenjs';

// Helper to parse text with links and return text objects with hyperlinks
const parseTextWithLinks = (text) => {
  if (!text) return [];

  const parts = [];
  let lastIndex = 0;

  // Find all markdown links [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    // Add text before link
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      if (beforeText.trim()) {
        parts.push({ text: beforeText, options: {} });
      }
    }
    // Add link
    parts.push({
      text: match[1], // Link text
      options: {
        hyperlink: {
          url: match[2],
          tooltip: match[2]
        },
        color: '1E88E5', // Blue color for links
        underline: true
      }
    });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText.trim()) {
      parts.push({ text: remainingText, options: {} });
    }
  }

  // If no links found, return as plain text
  if (parts.length === 0) {
    return [{ text: text || '', options: {} }];
  }

  return parts;
};

// Helper to extract links from text for footnotes
const extractLinks = (text) => {
  if (!text) return [];
  const links = [];
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;
  while ((match = markdownLinkRegex.exec(text)) !== null) {
    links.push({ text: match[1], url: match[2] });
  }
  return links;
};

// Helper to add text with links to a slide
const addTextWithLinks = (slide, text, options) => {
  const parts = parseTextWithLinks(text);

  // If no links, use simple addText
  if (parts.length === 1 && !parts[0].options.hyperlink) {
    slide.addText(parts[0].text, { ...options, ...parts[0].options });
    return;
  }

  // Build array of text runs for links (pptxgenjs format: array of objects)
  const textRuns = parts.map((part) => {
    const runOptions = {
      fontSize: options.fontSize || 12,
      color: part.options.hyperlink ? part.options.color : options.color || '1D1D1F',
      ...part.options
    };

    // Remove undefined properties
    Object.keys(runOptions).forEach((key) => {
      if (runOptions[key] === undefined) delete runOptions[key];
    });

    return {
      text: part.text,
      options: runOptions
    };
  });

  // Use addText with array format for hyperlinks
  // pptxgenjs expects: slide.addText([{text: '...', options: {...}}, ...], {x, y, w, h, ...})
  slide.addText(textRuns, options);
};

export async function exportGetWhoToBuyPptx(strategyData, fileName = 'get-who-to-buy-strategy.pptx') {
  const pptx = new pptxgen();
  pptx.layout = 'LAYOUT_16x9'; // Standard 16:9

  // Shape type constants - use pptxgen.shapes if available, otherwise fallback to string values
  const ShapeType = pptxgen.shapes || {
    RECTANGLE: 'rect',
    ELLIPSE: 'ellipse'
  };

  // Design System
  const theme = {
    colors: {
      primary: '1E88E5', // The specific blue requested
      primaryDark: '1565C0',
      text: '1D1D1F', // Almost black, softer than pure black
      textSecondary: '86868B',
      background: 'FFFFFF',
      surface: 'F5F5F7', // Apple-style light gray surface
      border: 'D2D2D7'
    },
    fonts: {
      heading: 'Arial',
      body: 'Arial'
    },
    margins: {
      x: 0.5,
      y: 0.5
    }
  };

  // --- Slide 1: Title Slide ---
  const titleSlide = pptx.addSlide();
  titleSlide.background = { color: theme.colors.background };

  // Client Name (Eyebrow)
  if (strategyData.client) {
    titleSlide.addText(strategyData.client.toUpperCase(), {
      x: 0.5,
      y: 2.2,
      w: '90%',
      h: 0.3,
      fontSize: 14,
      color: theme.colors.textSecondary,
      align: 'center',
      bold: true,
      charSpacing: 3 // Wide tracking
    });
  }

  // Main Title
  titleSlide.addText('Strategic Framework', {
    x: 0.5,
    y: 2.6,
    w: '90%',
    h: 1.0,
    fontSize: 54,
    color: theme.colors.text,
    align: 'center',
    bold: true
  });

  // Category (Subtitle)
  if (strategyData.category) {
    titleSlide.addText(strategyData.category, {
      x: 1.5,
      y: 3.6,
      w: '70%',
      h: 0.5,
      fontSize: 20,
      color: theme.colors.textSecondary,
      align: 'center'
    });
  }

  // Branding Footer
  titleSlide.addText('Powered by Askpot', {
    x: 0,
    y: 6.8,
    w: '100%',
    h: 0.4,
    fontSize: 12,
    color: theme.colors.textSecondary,
    align: 'center'
  });

  // --- Slide 2: GET / WHO / TO / BY Framework ---
  const frameworkSlide = pptx.addSlide();
  frameworkSlide.background = { color: theme.colors.background };

  // Slide Title
  frameworkSlide.addText('Strategic Framework', {
    x: 0.5,
    y: 0.4,
    w: 9,
    h: 0.5,
    fontSize: 24,
    color: theme.colors.text,
    bold: true
  });

  // Adjusted layout to fit all cards on screen (slide height is 5.625)
  const sections = [
    { label: 'GET', content: strategyData.get, x: 0.5, y: 1.0 },
    { label: 'WHO', content: strategyData.who, x: 5.1, y: 1.0 },
    { label: 'TO', content: strategyData.to, x: 0.5, y: 3.3 },
    { label: 'BY', content: strategyData.by, x: 5.1, y: 3.3 }
  ];

  sections.forEach((section) => {
    if (!section.content) return;

    // Card Background - reduced height to fit on slide
    frameworkSlide.addShape(ShapeType.RECTANGLE, {
      x: section.x,
      y: section.y,
      w: 4.4,
      h: 2.1,
      fill: { color: theme.colors.surface },
      line: { color: 'transparent' }
    });

    // Section Header (Icon-like circle + Label)
    frameworkSlide.addShape(ShapeType.ELLIPSE, {
      x: section.x + 0.2,
      y: section.y + 0.2,
      w: 0.4,
      h: 0.4,
      fill: { color: theme.colors.primary },
      line: { color: 'transparent' }
    });

    frameworkSlide.addText(section.label, {
      x: section.x + 0.7,
      y: section.y + 0.2,
      w: 3.5,
      h: 0.4,
      fontSize: 18,
      color: theme.colors.text,
      bold: true,
      valign: 'middle'
    });

    // Content with links preserved - adjusted height
    addTextWithLinks(frameworkSlide, section.content, {
      x: section.x + 0.2,
      y: section.y + 0.7,
      w: 4.0,
      h: 1.3,
      fontSize: 11,
      color: theme.colors.text,
      valign: 'top',
      lineSpacing: 16
    });
  });

  // --- Slide 3: Key Strategic Insight ---
  if (strategyData.key_strategic_insight) {
    const insightSlide = pptx.addSlide();
    insightSlide.background = { color: theme.colors.background };

    // Slide Title
    insightSlide.addText('Key Strategic Insight', {
      x: 0.5,
      y: 0.4,
      w: 9,
      h: 0.5,
      fontSize: 24,
      color: theme.colors.text,
      bold: true
    });

    // Large distinctive card - adjusted to fit on slide
    insightSlide.addShape(ShapeType.RECTANGLE, {
      x: 0.5,
      y: 1.1,
      w: 9,
      h: 4.2,
      fill: { color: theme.colors.surface },
      line: { color: theme.colors.primary, width: 3 } // Accent border
    });

    // Content with links preserved - adjusted to fit
    addTextWithLinks(insightSlide, strategyData.key_strategic_insight, {
      x: 1.0,
      y: 1.5,
      w: 8.0,
      h: 3.2,
      fontSize: 18, // Slightly smaller to fit
      color: theme.colors.text,
      align: 'left', // Left align for better readability
      valign: 'top',
      lineSpacing: 26
    });
  }

  // --- Slide 4: Creative Implication ---
  if (strategyData.creative_implication) {
    const creativeSlide = pptx.addSlide();
    creativeSlide.background = { color: theme.colors.background };

    creativeSlide.addText('Creative Implication', {
      x: 0.5,
      y: 0.4,
      w: 9,
      h: 0.5,
      fontSize: 24,
      color: theme.colors.text,
      bold: true
    });

    // Card styling - adjusted to fit on slide
    creativeSlide.addShape(ShapeType.RECTANGLE, {
      x: 0.5,
      y: 1.1,
      w: 9,
      h: 4.2,
      fill: { color: theme.colors.surface },
      line: { color: 'transparent' }
    });

    // Content with links preserved - adjusted to fit
    addTextWithLinks(creativeSlide, strategyData.creative_implication, {
      x: 1.0,
      y: 1.5,
      w: 8.0,
      h: 3.2,
      fontSize: 16,
      color: theme.colors.text,
      valign: 'top',
      lineSpacing: 24
    });
  }

  // --- Slide 5+: Supporting Data Points (Paginated) ---
  if (strategyData.supporting_data_points && strategyData.supporting_data_points.length > 0) {
    const itemsPerPage = 4; // Reduced from 5 to ensure all items fit
    const totalDataSlides = Math.ceil(strategyData.supporting_data_points.length / itemsPerPage);

    for (let i = 0; i < totalDataSlides; i++) {
      const slide = pptx.addSlide();
      slide.background = { color: theme.colors.background };

      slide.addText('Supporting Data Points', {
        x: 0.5,
        y: 0.4,
        w: 9,
        h: 0.5,
        fontSize: 24,
        color: theme.colors.text,
        bold: true
      });

      const startIdx = i * itemsPerPage;
      const pageItems = strategyData.supporting_data_points.slice(startIdx, startIdx + itemsPerPage);

      let yPos = 1.1;
      const itemHeight = (5.625 - 1.1 - 0.3) / pageItems.length; // Distribute remaining space
      pageItems.forEach((item) => {
        // Bullet shape
        slide.addShape(ShapeType.ELLIPSE, {
          x: 0.5,
          y: yPos + 0.1,
          w: 0.1,
          h: 0.1,
          fill: { color: theme.colors.primary },
          line: { color: 'transparent' }
        });

        // Text with links preserved - dynamic height
        addTextWithLinks(slide, item, {
          x: 0.8,
          y: yPos,
          w: 8.7,
          h: itemHeight - 0.1,
          fontSize: 13,
          color: theme.colors.text,
          valign: 'top',
          lineSpacing: 18
        });

        yPos += itemHeight; // Spacing between items
      });
    }
  }

  // --- Slide 6+: Tone & Channel Direction (Paginated) ---
  if (strategyData.tone_channel_direction) {
    const entries = Object.entries(strategyData.tone_channel_direction).filter((e) => e[1]);
    const itemsPerPage = 2; // Reduced from 3 to ensure content fits
    const totalSlides = Math.ceil(entries.length / itemsPerPage);

    for (let i = 0; i < totalSlides; i++) {
      const slide = pptx.addSlide();
      slide.background = { color: theme.colors.background };

      slide.addText('Tone & Channel Direction', {
        x: 0.5,
        y: 0.4,
        w: 9,
        h: 0.5,
        fontSize: 24,
        color: theme.colors.text,
        bold: true
      });

      const startIdx = i * itemsPerPage;
      const pageEntries = entries.slice(startIdx, startIdx + itemsPerPage);

      let yPos = 1.1;
      const itemHeight = (5.625 - 1.1 - 0.3) / pageEntries.length; // Distribute remaining space
      pageEntries.forEach(([key, value]) => {
        const title = key
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');

        // Item Container - dynamic height
        slide.addShape(ShapeType.RECTANGLE, {
          x: 0.5,
          y: yPos,
          w: 9,
          h: itemHeight - 0.1,
          fill: { color: theme.colors.surface },
          line: { color: 'transparent' }
        });

        // Title
        slide.addText(title, {
          x: 0.7,
          y: yPos + 0.1,
          w: 8.6,
          h: 0.3,
          fontSize: 14,
          color: theme.colors.primary,
          bold: true
        });

        // Body with links preserved - dynamic height
        addTextWithLinks(slide, value, {
          x: 0.7,
          y: yPos + 0.5,
          w: 8.6,
          h: itemHeight - 0.6,
          fontSize: 12,
          color: theme.colors.text,
          valign: 'top',
          lineSpacing: 18
        });

        yPos += itemHeight;
      });
    }
  }

  // --- Slide 7+: Strategic Guardrails ---
  if (strategyData.strategic_guardrails) {
    const entries = Object.entries(strategyData.strategic_guardrails).filter((e) => e[1]);
    const itemsPerPage = 2; // Reduced from 3 to ensure content fits
    const totalSlides = Math.ceil(entries.length / itemsPerPage);

    for (let i = 0; i < totalSlides; i++) {
      const slide = pptx.addSlide();
      slide.background = { color: theme.colors.background };

      slide.addText('Strategic Guardrails', {
        x: 0.5,
        y: 0.4,
        w: 9,
        h: 0.5,
        fontSize: 24,
        color: theme.colors.text,
        bold: true
      });

      const startIdx = i * itemsPerPage;
      const pageEntries = entries.slice(startIdx, startIdx + itemsPerPage);

      let yPos = 1.1;
      const itemHeight = (5.625 - 1.1 - 0.3) / pageEntries.length; // Distribute remaining space
      pageEntries.forEach(([key, value]) => {
        const title = key
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');

        // Item Container - dynamic height
        slide.addShape(ShapeType.RECTANGLE, {
          x: 0.5,
          y: yPos,
          w: 9,
          h: itemHeight - 0.1,
          fill: { color: theme.colors.surface },
          line: { color: 'transparent' }
        });

        // Title
        slide.addText(title, {
          x: 0.7,
          y: yPos + 0.1,
          w: 8.6,
          h: 0.3,
          fontSize: 14,
          color: 'D32F2F', // Red/Warning color for guardrails
          bold: true
        });

        // Body with links preserved - dynamic height
        addTextWithLinks(slide, value, {
          x: 0.7,
          y: yPos + 0.5,
          w: 8.6,
          h: itemHeight - 0.6,
          fontSize: 12,
          color: theme.colors.text,
          valign: 'top',
          lineSpacing: 18
        });

        yPos += itemHeight;
      });
    }
  }

  // --- Footnotes Slide (if needed) ---
  // Collect all links for a reference slide
  const allLinks = [];
  if (strategyData.key_strategic_insight) allLinks.push(...extractLinks(strategyData.key_strategic_insight));
  if (strategyData.creative_implication) allLinks.push(...extractLinks(strategyData.creative_implication));
  if (strategyData.supporting_data_points) {
    strategyData.supporting_data_points.forEach((point) => {
      allLinks.push(...extractLinks(point));
    });
  }

  if (allLinks.length > 0) {
    const linksSlide = pptx.addSlide();
    linksSlide.background = { color: theme.colors.background };

    linksSlide.addText('References', {
      x: 0.5,
      y: 0.4,
      w: 9,
      h: 0.5,
      fontSize: 24,
      color: theme.colors.text,
      bold: true
    });

    let yPos = 1.2;
    allLinks.forEach((link) => {
      if (yPos > 6.5) return; // Simple overflow check
      // Add clickable hyperlinks
      linksSlide.addText(link.text, {
        x: 0.5,
        y: yPos,
        w: 9,
        h: 0.3,
        fontSize: 11,
        color: theme.colors.primary,
        hyperlink: {
          url: link.url,
          tooltip: link.url
        },
        underline: true
      });
      yPos += 0.4;
    });
  }

  // Save
  await pptx.writeFile({ fileName });
}

export default exportGetWhoToBuyPptx;
